import { useMemo } from 'react'
import { StreamStep } from 'src/v3/core/types'
import {
  CourseAccreditationResponse,
  CourseV5Details,
  InstructorWithCourses,
  ObjectivesReference,
  QuizLibraryItem,
} from '../types'

type PublishChecks = {
  basicInfo: boolean
  syllabus: boolean
  hasContentStep: boolean
  lastStepIsQuiz: boolean
  quizzesHaveQuestions: boolean
  accreditation: boolean
  hasInstructor: boolean
  noUnsavedChanges: boolean
}

type PublishValidation = {
  canPublish: boolean
  checks: PublishChecks
}

export function usePublishValidation(
  courseDetails: CourseV5Details | null,
  courseSteps: StreamStep[],
  courseQuizzes: QuizLibraryItem[],
  courseInstructors: InstructorWithCourses[],
  accreditation: CourseAccreditationResponse | null,
  hasDirtyChanges: boolean,
): PublishValidation {
  let hasBasicInfo = useMemo(
    () =>
      !!(
        courseDetails?.name &&
        courseDetails?.linkedServiceID &&
        courseDetails?.sectionID &&
        courseDetails?.templateID &&
        courseDetails?.length != null &&
        courseDetails?.length >= 0
      ),
    [
      courseDetails?.name,
      courseDetails?.linkedServiceID,
      courseDetails?.sectionID,
      courseDetails?.templateID,
      courseDetails?.length,
    ],
  )

  let hasSyllabus = useMemo(() => {
    if (!courseDetails?.description) return false
    let objectivesValid = (courseDetails.objectives ?? []).every(
      (obj: ObjectivesReference) => obj.text.trim() !== '',
    )
    let referencesValid = (courseDetails.references ?? []).every(
      (ref: ObjectivesReference) => ref.text.trim() !== '',
    )
    return objectivesValid && referencesValid
  }, [
    courseDetails?.description,
    courseDetails?.objectives,
    courseDetails?.references,
  ])

  let hasContentStep = useMemo(
    () => courseSteps.some((s) => !!s.videoID || !!s.showNotesID),
    [courseSteps],
  )

  let lastStepIsQuiz = useMemo(() => {
    if (courseSteps.length === 0) return false
    let lastStep = courseSteps.reduce((prev, curr) =>
      (curr.stepIndex ?? 0) > (prev.stepIndex ?? 0) ? curr : prev,
    )
    return !!lastStep.quizID
  }, [courseSteps])

  let quizzesHaveQuestions = useMemo(() => {
    let activeQuizIDs = new Set(
      courseSteps.filter((s) => s.quizID).map((s) => s.quizID as string),
    )
    if (activeQuizIDs.size === 0) return true
    let matchedQuizzes = courseQuizzes.filter((q) => activeQuizIDs.has(q.id))
    return (
      matchedQuizzes.length === activeQuizIDs.size &&
      matchedQuizzes.every((q) => q.questionCount > 0)
    )
  }, [courseSteps, courseQuizzes])

  let hasAccreditation = accreditation?.version === 2

  let hasInstructor = courseInstructors.length > 0

  let noUnsavedChanges = !hasDirtyChanges

  let canPublish = useMemo(
    () =>
      !!courseDetails &&
      hasBasicInfo &&
      hasSyllabus &&
      hasContentStep &&
      lastStepIsQuiz &&
      quizzesHaveQuestions &&
      hasAccreditation &&
      hasInstructor &&
      noUnsavedChanges,
    [
      courseDetails,
      hasBasicInfo,
      hasSyllabus,
      hasContentStep,
      lastStepIsQuiz,
      quizzesHaveQuestions,
      hasAccreditation,
      hasInstructor,
      noUnsavedChanges,
    ],
  )

  return {
    canPublish,
    checks: {
      basicInfo: hasBasicInfo,
      syllabus: hasSyllabus,
      hasContentStep,
      lastStepIsQuiz,
      quizzesHaveQuestions,
      accreditation: hasAccreditation,
      hasInstructor,
      noUnsavedChanges,
    },
  }
}
